import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, Loader2, Zap, Mail, FileText, 
  TrendingUp, Calendar, DollarSign, Clock, ArrowRight
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function AutoBooksOnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [moduleId, setModuleId] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [progress, setProgress] = useState(20);

  useEffect(() => {
    initializeModule();
  }, []);

  const initializeModule = async () => {
    try {
      const response = await axios.post(
        `${API}/onboarding/autobooks/initialize`,
        {
          business_type: "accountant",
          primary_email_tool: "gmail",
          comfort_level: "beginner"
        },
        { withCredentials: true }
      );
      setModuleId(response.data.module_id);
      setProgress(20);
    } catch (error) {
      console.error('Error initializing AutoBooks:', error);
      toast.error("Failed to initialize onboarding");
    }
  };

  const handleConnectQuickBooks = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/autobooks/quickbooks/connect`,
        {},
        { withCredentials: true }
      );
      toast.success("QuickBooks connected!");
      setProgress(40);
      setCurrentStep(2);
      // Auto-start email analysis
      setTimeout(() => handleAnalyzeEmails(), 1000);
    } catch (error) {
      console.error('Error connecting QuickBooks:', error);
      toast.error("Failed to connect QuickBooks");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/autobooks/analyze-emails`,
        { gmail_access: true, sample_mode: false },
        { withCredentials: true }
      );
      setAnalysisResults(response.data);
      setSuggestions(response.data.automation_suggestions);
      setProgress(60);
      setCurrentStep(3);
      toast.success("Email analysis complete!");
    } catch (error) {
      console.error('Error analyzing emails:', error);
      toast.error("Failed to analyze emails");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFirstAutomation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/autobooks/automation/create`,
        {
          name: "Invoice Processing Automation",
          description: "Automatically extract invoice data and create QuickBooks entries",
          nodes: [],
          edges: []
        },
        { withCredentials: true }
      );
      
      setProgress(100);
      setCurrentStep(5);
      
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      toast.success("First automation created!");
    } catch (error) {
      console.error('Error creating automation:', error);
      toast.error("Failed to create automation");
    } finally {
      setLoading(false);
    }
  };

  const renderWelcomeStep = () => (
    <div className="space-y-8">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <FileText className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-4xl font-heading font-semibold text-foreground mb-4">
          Welcome to AutoBooks
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          The intelligent automation assistant for accountants and bookkeepers.
          Save 12-15 hours per week by automating invoice processing, expense tracking, and more.
        </p>
      </div>

      <Card className="glass-card border-primary/30 max-w-3xl mx-auto">
        <CardContent className="p-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Save Time</h3>
              <p className="text-sm text-muted-foreground">12-15 hours saved per week</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Increase Value</h3>
              <p className="text-sm text-muted-foreground">$2,500-4,000/month value</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Quick Setup</h3>
              <p className="text-sm text-muted-foreground">Under 10 minutes to start</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <Button
          data-testid="connect-quickbooks-btn"
          onClick={handleConnectQuickBooks}
          disabled={loading}
          className="rounded-full px-8 h-12 text-lg bg-primary hover:bg-primary/90"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting...
            </>
          ) : (
            <>
              Connect QuickBooks <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
        <p className="text-sm text-muted-foreground mt-4">
          We'll analyze your invoices to set up automation
        </p>
      </div>
    </div>
  );

  const renderAnalysisStep = () => (
    <div className="space-y-8 max-w-3xl mx-auto">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
        <h2 className="text-3xl font-heading font-semibold text-foreground mb-4">
          Analyzing Your Inbox
        </h2>
        <p className="text-muted-foreground">
          AI is scanning your emails to find automation opportunities...
        </p>
      </div>

      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-foreground">Finding invoice emails</span>
            </div>
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <span className="text-foreground">Identifying expense reports</span>
            </div>
            <div className="flex items-center gap-3 opacity-50">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <span className="text-muted-foreground">Detecting tax documents</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderFindingsStep = () => (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-3xl font-heading font-semibold text-foreground mb-4">
          Here's What We Found
        </h2>
        <p className="text-muted-foreground">
          Based on your email analysis, we've identified automation opportunities
        </p>
      </div>

      {analysisResults && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="glass-card border-primary/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-heading font-semibold text-primary mb-2">
                {analysisResults.patterns_found.invoice_emails.count}
              </div>
              <p className="text-sm text-muted-foreground">Invoice emails we can automate</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-primary/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-heading font-semibold text-primary mb-2">
                {analysisResults.patterns_found.expense_reports.count}
              </div>
              <p className="text-sm text-muted-foreground">Expense reports ready to process</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-primary/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-heading font-semibold text-primary mb-2">
                {analysisResults.patterns_found.tax_documents.count}
              </div>
              <p className="text-sm text-muted-foreground">Tax documents to organize</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="glass-card border-primary/50">
        <CardHeader>
          <CardTitle className="font-heading">Recommended Automations</CardTitle>
          <CardDescription>We suggest starting with these high-impact automations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {suggestions.slice(0, 2).map((suggestion, index) => (
            <div key={index} className="p-4 rounded-xl bg-muted/30 border border-border/50">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-semibold text-foreground">
                      {suggestion.suggestion_name}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {suggestion.estimated_hours_saved}h/week saved
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{suggestion.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={suggestion.confidence_score * 100} className="flex-1" />
                <span className="text-sm text-muted-foreground">
                  {Math.round(suggestion.confidence_score * 100)}% confidence
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="text-center">
        <Button
          data-testid="setup-first-automation-btn"
          onClick={() => setCurrentStep(4)}
          className="rounded-full px-8 h-12 text-lg bg-primary hover:bg-primary/90"
        >
          Set Up First Automation <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderSetupStep = () => (
    <div className="space-y-8 max-w-3xl mx-auto">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Zap className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-3xl font-heading font-semibold text-foreground mb-4">
          Let's Create Your First Automation
        </h2>
        <p className="text-muted-foreground">
          Invoice Processing - Save 10 hours per week
        </p>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-heading">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="font-semibold text-primary">1</span>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Trigger: Email with PDF Invoice</h4>
              <p className="text-sm text-muted-foreground">
                Automatically detects emails containing invoice PDFs
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="font-semibold text-primary">2</span>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Extract Data</h4>
              <p className="text-sm text-muted-foreground">
                AI extracts amount, invoice #, date, and vendor information
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="font-semibold text-primary">3</span>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Create QuickBooks Entry</h4>
              <p className="text-sm text-muted-foreground">
                Automatically creates entry in QuickBooks with extracted data
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center space-y-4">
        <Button
          data-testid="create-automation-btn"
          onClick={handleCreateFirstAutomation}
          disabled={loading}
          className="rounded-full px-8 h-12 text-lg bg-primary hover:bg-primary/90"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...
            </>
          ) : (
            <>
              Create Automation <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
        <p className="text-sm text-muted-foreground">
          You can customize this later in the workflow builder
        </p>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="space-y-8 max-w-3xl mx-auto text-center">
      <div>
        <div className="w-20 h-20 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-4xl font-heading font-semibold text-foreground mb-4">
          🎉 Your First Automation is Live!
        </h2>
        <p className="text-lg text-muted-foreground">
          Invoice processing is now active and working in the background
        </p>
      </div>

      <Card className="glass-card border-green-500/30">
        <CardContent className="p-8">
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <div className="text-4xl font-heading font-semibold text-green-500 mb-2">
                10 hours
              </div>
              <p className="text-sm text-muted-foreground">Saved per week</p>
            </div>
            <div className="w-px h-16 bg-border" />
            <div className="text-center">
              <div className="text-4xl font-heading font-semibold text-green-500 mb-2">
                $500
              </div>
              <p className="text-sm text-muted-foreground">Value per week</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-heading">What's Next?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-start h-auto py-4"
            onClick={() => navigate('/autobooks/library')}
          >
            <Zap className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">Add More Automations</div>
              <div className="text-sm text-muted-foreground">Explore 4 more automation templates</div>
            </div>
          </Button>
          <Button
            data-testid="view-automation-btn"
            variant="outline"
            className="w-full justify-start h-auto py-4"
            onClick={() => navigate('/workflows')}
          >
            <FileText className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">View Your Automation</div>
              <div className="text-sm text-muted-foreground">Customize and monitor</div>
            </div>
          </Button>
          <Button
            data-testid="go-to-dashboard-btn"
            variant="outline"
            className="w-full justify-start h-auto py-4"
            onClick={() => navigate('/dashboard')}
          >
            <TrendingUp className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-semibold">Go to Dashboard</div>
              <div className="text-sm text-muted-foreground">See your impact metrics</div>
            </div>
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              AutoBooks Setup Progress
            </span>
            <span className="text-sm text-muted-foreground">
              {progress}% Complete
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-24">
        {currentStep === 1 && renderWelcomeStep()}
        {currentStep === 2 && renderAnalysisStep()}
        {currentStep === 3 && renderFindingsStep()}
        {currentStep === 4 && renderSetupStep()}
        {currentStep === 5 && renderSuccessStep()}
      </div>
    </div>
  );
}
